import { type CSSObject } from '@emotion/react';
import { type Theme } from '../../../theme';
import { type ButtonSize, type ButtonType } from '../button.types';

const getGenericButtonStyles = (theme: Theme): CSSObject => ({
  fontFamily: theme.typography.fontFamily,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  borderWidth: 1,
  borderStyle: 'solid',
});

const getVariant = (theme: Theme, size: ButtonSize): CSSObject => {
  switch (size) {
    case 'regular': {
      return {
        gap: theme.spacing(2),
        height: 40,
        padding: theme.spacing(1, 3),
        borderRadius: theme.shape.radius8,
      };
    }
    case 'small': {
      return {
        gap: theme.spacing(1),
        height: 32,
        padding: theme.spacing(1, 2),
        borderRadius: theme.shape.radius8,
      };
    }
    case 'xsmall': {
      return {
        gap: theme.spacing(1),
        height: 24,
        padding: `2px ${theme.spacing(2)}`,
        borderRadius: theme.shape.radius4,
      };
    }
  }
};

export const getStatusButton = ({
  theme,
  type,
  size,
}: {
  theme: Theme;
  type: ButtonType;
  size: ButtonSize;
}): CSSObject | null => {
  const genericButtonStyles = getGenericButtonStyles(theme);
  const variant = getVariant(theme, size);
  switch (type) {
    case 'primary':
    case 'upgrade': {
      return {
        ...genericButtonStyles,
        ...variant,
        color: theme.button.loading.label,
        backgroundColor: theme.button.secondary.disabled,
        borderColor: theme.button.primary.border,
        minWidth: size === 'regular' ? 100 : undefined,

        '&:hover, &:active, &:focus:not(:focus-visible)': {
          backgroundColor: theme.button.primary.disabled,
        },
        '&:focus': {
          backgroundColor: theme.button.primary.disabled,
        },
        '&:focus-visible': {
          backgroundColor: theme.button.primary.disabled,
          outline: `2.5px solid ${theme.button.focused.default}`,
          outlineOffset: 4,
        },
      };
    }
    case 'secondary':
    case 'destructive': {
      return {
        ...genericButtonStyles,
        ...variant,
        color: theme.button.loading.label,
        backgroundColor: theme.button.secondary.disabled,
        border: `1px solid ${theme.button.secondary.border}`,

        '&:hover, &:active, &:focus:not(:focus-visible)': {
          backgroundColor: theme.button.secondary.disabled,
        },
        '&:focus': {
          backgroundColor: theme.button.secondary.disabled,
        },
        '&:focus-visible': {
          backgroundColor: theme.button.secondary.disabled,
          outline: `2.5px solid ${theme.button.focused.default}`,
          outlineOffset: 4,
        },
      };
    }
    case 'secondaryAlt': {
      return {
        ...genericButtonStyles,
        ...variant,
        color: theme.button.loading.label,
        backgroundColor: theme.button.secondaryAlt.default,
        borderColor: theme.button.secondaryAlt.border,

        '&:hover, &:active, &:focus:not(:focus-visible)': {
          backgroundColor: theme.button.secondaryAlt.default,
        },
        '&:focus': {
          backgroundColor: theme.button.secondaryAlt.default,
        },
        '&:focus-visible': {
          backgroundColor: theme.button.secondaryAlt.default,
          outline: `2.5px solid ${theme.button.focused.default}`,
          outlineOffset: 4,
        },
      };
    }
    case 'outline':
    case 'outlineDestructive': {
      return {
        ...genericButtonStyles,
        ...variant,
        color: theme.button.loading.label,
        backgroundColor: 'transparent',
        borderColor: theme.button.outline.default,
        '&:hover, &:active, &:focus:not(:focus-visible)': {
          backgroundColor: 'transparent',
        },
        '&:focus': {
          backgroundColor: 'transparent',
        },
        '&:focus-visible': {
          backgroundColor: 'transparent',
          outline: `2.5px solid ${theme.button.focused.default}`,
          outlineOffset: 4,
        },
      };
    }
    case 'link':
    case 'linkInline':
    case 'ghost': {
      return {
        ...genericButtonStyles,
        ...variant,
        color: theme.button.loading.label,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        '&:hover, &:active, &:focus:not(:focus-visible)': {
          backgroundColor: 'transparent',
        },
        '&:focus': {
          backgroundColor: 'transparent',
        },
        '&:focus-visible': {
          backgroundColor: 'transparent',
          outline: `2.5px solid ${theme.button.focused.default}`,
          outlineOffset: 4,
        },
      };
    }
    default: {
      return null;
    }
  }
};
